import React, { useEffect } from 'react';
import { errorBoundary, withInject, view } from '@piwikpro/platform';
import {
  View,
  withTranslation,
  Layout,
  Card,
  Stack,
} from '@piwikpro/ui-components';
import { AuthSettings } from '@piwikpro/auth-crate';
import CookieInformationIntegrationTokenComponent from '@piwikpro/cookie-information-crate/components/CookieInformationIntegrationToken/CookieInformationIntegrationToken';
import { Organization } from '@piwikpro/organization-crate';
import Saml from './Saml';
import TwoFactorAuthentication from './TwoFactorAuthentication';
import TechnicalSupport from './TechnicalSupport';
import SessionLength from './SessionLength';

interface AccountSettingsProps {
  Title: React.ElementType
  LogoAndFavicon: React.ElementType
  t: (key: string) => string
  authSettings: AuthSettings
  organization: Organization
  CookieInformationIntegrationToken: typeof CookieInformationIntegrationTokenComponent
}

const AccountSettings: React.FunctionComponent<AccountSettingsProps> = ({
  Title,
  LogoAndFavicon,
  CookieInformationIntegrationToken,
  authSettings,
  organization,
  t,
}) => {
  useEffect(() => {
    const fetchData = async () => {
      try {
        await Promise.all([
          authSettings.getSettingsForOrganization(),
          organization.fetchDetails(),
        ]);
        // eslint-disable-next-line no-empty
      } catch { }
    };

    fetchData();
  }, []);

  return (
    <Title part={t('platform.accountSettings.title')}>
      <View>
        <View.Header title={t('platform.accountSettings.title')} />
        <View.Body>
          <Layout>
            <Layout.AnnotatedSection title={t('platform.accountSettings.security.title')} />
            <Layout.Column dominant>
              <Stack vertical spacing="wide">
                <Stack.Item fill>
                  <Saml />
                </Stack.Item>
                <Stack.Item fill>
                  <Card>
                    <Card.Section>
                      <TwoFactorAuthentication />
                    </Card.Section>
                  </Card>
                </Stack.Item>
                <TechnicalSupport />
              </Stack>
            </Layout.Column>
          </Layout>
          <Layout dataId="SessionLengthSettings">
            <Layout.AnnotatedSection
              title={t('platform.accountSettings.sessionLength.title')}
            />
            <Layout.Column dominant>
              <Card>
                <Card.Section>
                  <SessionLength />
                </Card.Section>
              </Card>
            </Layout.Column>
          </Layout>
          <Layout dataId="Integrations">
            <Layout.AnnotatedSection title={t('platform.accountSettings.integrations')} />
            <Layout.Column dominant>
              <CookieInformationIntegrationToken />
            </Layout.Column>
          </Layout>
          <LogoAndFavicon />
        </View.Body>
      </View>
    </Title>
  );
};

export default withInject({
  Title: 'TitleCrate.components.Title',
  authSettings: 'AuthCrate.authSettings',
  organization: 'OrganizationCrate.organization',
  LogoAndFavicon: 'OrganizationCrate.components.LogoAndFavicon',
  CookieInformationIntegrationToken: 'CookieInformationCrate.components.CookieInformationIntegrationToken',
})(view({ role: 'OWNER' })(withTranslation('administration')(errorBoundary(AccountSettings))));
